/* eslint-disable max-lines -- this class will be decoed once we completely switch to objects */
import { isDefined } from '@sama/js-core';
import {
  flatten,
  get,
  isEmpty,
  omit,
  uniqBy,
  uniqueId,
} from '@sama/micro-dash';
import { contentUtils2D } from '../../../../2d/exported/content-utils-2d';
import { contentUtils3D } from '../../../../3d/exported/content-utils-3d';
import {
  isShapeLocationExternal,
  isTagLocationExternal,
  ShapeLocationState,
} from '../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import {
  addDynamicTagsToKeyLocations,
  extractDynamicTagsFromKeyLocation,
} from '../../../../cross-team-shared/misc-services/tag-helper';
import {
  isImageAnnotationOutput,
  isPointCloudOutput,
} from '../../../../cross-team-shared/misc-utils/output-utils';
import {
  ShapeGroupState,
  ShapeOrGroupOrderItem,
} from '../../../../cross-team-shared/state/shape-group-state';
import { pickExisting } from '../../../../shared/misc-utilities/misc-utils';
import { ShapeGroupOrderState } from '../../../../team-apis/content/content-connector';
import {
  ContentUtils,
  PartiallyTransformedShape,
} from '../../../../team-apis/content/content-utils';
import { Answer } from '../../../../team-apis/otterdam/answers/answer';
import {
  LocationSource,
  PointType,
  ShapeExternal,
  ShapeGroupExternal,
  ShapeLayerExternalObjectless,
  ShapeLocation,
  ShapeLocationExternal,
  ShapeType,
  VectorTaggingLayerExternal,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { AnnotationOutput } from '../../../../team-apis/otterdam/outputs/annotation-output';
import { Task } from '../../../../team-apis/otterdam/task';
import { filterOutOrthographicShapes } from './fusion/projection/orthographic-orchestrator';

export class ShapeLayerAnswerHelper<P extends PointType, S extends ShapeType> {
  constructor(private contentUtils: ContentUtils<P, S>) {}

  answerToShapeGroupOrder(
    answer:
      | ShapeLayerExternalObjectless<P, S>
      | StrictRecord<string, never>
      | undefined,
  ): ShapeGroupOrderState {
    const shapes = this.answerToShapes(answer);
    const groupAndOrder = this.answerToGroupOrder(answer);
    return { shapes, ...groupAndOrder };
  }

  answerToShapes(
    answer:
      | ShapeLayerExternalObjectless<P, S>
      | StrictRecord<string, never>
      | undefined,
  ) {
    if (!this.answerIsNonBlank(answer)) {
      return {};
    }
    const shapes: StrictRecord<ShapeState['index'], ShapeState> = {};
    const sources = answer.sources;
    for (const shapeGroupExternal of answer.shape_groups) {
      for (const shapeExternal of shapeGroupExternal.shapes) {
        shapes[shapeExternal.index] = this.shapeExternalToState(
          shapeExternal,
          sources,
        );
      }
    }
    return shapes;
  }

  // eslint-disable-next-line max-lines-per-function -- will remove this entire class, once all projects transition to objects
  answerToGroupOrder(
    answer:
      | ShapeLayerExternalObjectless<P, S>
      | StrictRecord<string, never>
      | undefined,
  ) {
    if (!this.answerIsNonBlank(answer)) {
      return { groups: {}, shapeAndGroupOrder: [] };
    }

    const groups: StrictRecord<ShapeGroupState['index'], ShapeGroupState> = {};
    const shapeAndGroupOrder: ShapeOrGroupOrderItem[] = [];
    for (const shapeGroupExternal of answer.shape_groups) {
      if (hasGroupType<P, S>(shapeGroupExternal)) {
        const index = +uniqueId();
        groups[index] = {
          index,
          type: shapeGroupExternal.group_type,
          shapeIndexes: shapeGroupExternal.shapes.map(
            (shapeExternal) => shapeExternal.index,
          ),
        };
        shapeAndGroupOrder.push({ type: 'group', index });
      } else {
        shapeAndGroupOrder.push({
          type: 'shape',
          index: shapeGroupExternal.shapes[0].index,
        });
      }
    }
    return { groups, shapeAndGroupOrder };
  }

  shapeGroupOrderExternal(
    shapeGroupOrder: ShapeGroupOrderState<S, P>,
    frameCount = 1,
  ) {
    const sources = this.extractSources(shapeGroupOrder);

    const shapeGroups = shapeGroupOrder.shapeAndGroupOrder.map((item) => {
      if (item.type === 'group') {
        const group = shapeGroupOrder.groups[item.index];
        return this.makeGroupExternal(
          shapeGroupOrder,
          // @ts-ignore -- fix me
          group.shapeIndexes,
          sources,
          // @ts-ignore -- fix me
          group.type,
        );
      } else {
        return this.makeGroupExternal(shapeGroupOrder, [item.index], sources);
      }
    });

    return {
      frame_count: frameCount,
      shape_groups: shapeGroups,
      sources,
    };
  }

  shapeStateExternal(
    shapeState: ShapeState<P, S>,
    sources: LocationSource[],
  ): ShapeExternal<P, S> {
    const partiallyTransformedShape =
      this.contentUtils.convertShapeStateToAnswer(shapeState);
    const keyLocationsForStore = this.convertExtrapolatedPointsToKeyLocations(
      partiallyTransformedShape,
    );
    const shapeLocationsExternal = this.shapeSourcesStoreExternal(
      keyLocationsForStore,
      sources,
    );
    const locationsExternal = addDynamicTagsToKeyLocations(
      // @ts-ignore -- fix me
      partiallyTransformedShape.dynamic_tags,
      shapeLocationsExternal,
    );

    return {
      ...omit(partiallyTransformedShape, 'dynamic_tags'),
      key_locations: locationsExternal,
    };
  }

  private shapeExternalToState(
    shapeExternal: ShapeExternal<P, S>,
    sources: LocationSource[] | undefined,
  ) {
    const keyLocations: Array<ShapeLocationState<P>> = [];
    const dynamicTags: ShapeState['dynamic_tags'] = {};

    // Convert key_locations from a ShapeLocationExternal
    // to a ShapeLocationForState. It's slightly different
    for (const keyLocation of shapeExternal.key_locations) {
      if (isShapeLocationExternal(keyLocation)) {
        this.addShapeLocation(keyLocation, keyLocations, sources ?? []);
      }
      if (isTagLocationExternal(keyLocation)) {
        extractDynamicTagsFromKeyLocation(keyLocation, dynamicTags);
      }
    }

    return this.contentUtils.addKeyLocationsToShapeExternal({
      ...shapeExternal,
      dynamic_tags: dynamicTags,
      key_locations: keyLocations,
    });
  }

  private extractSources(shapeGroupOrder: ShapeGroupOrderState<S, P>) {
    const keyLocations = flatten(
      // @ts-ignore -- fix me
      Object.values(shapeGroupOrder.shapes).map((shape) => shape.key_locations),
    );

    const sources = flatten([
      keyLocations.map((keyLocation) => keyLocation.source),
      keyLocations.map((keyLocation) => keyLocation.extrapolation?.source),
    ])
      .map((keyLocation) => keyLocation)
      .filter(isDefined);

    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    return uniqBy(sources, (source) => source?.name);
  }

  private shapeSourcesStoreExternal(
    keyLocations: Array<ShapeLocation<P>>,
    sources: LocationSource[],
  ) {
    const keyLocationsExternal: Array<ShapeLocationExternal<P>> = [];
    for (const keyLocation of keyLocations) {
      if (keyLocation.source !== undefined) {
        const index = sources.findIndex(
          (source) => keyLocation.source?.model_id === source.model_id,
        );
        keyLocationsExternal.push({ ...keyLocation, source: index });
      } else {
        keyLocationsExternal.push(omit(keyLocation, 'source'));
      }
    }
    return keyLocationsExternal;
  }

  private convertExtrapolatedPointsToKeyLocations(
    shapeState: PartiallyTransformedShape<P, S>,
  ) {
    const updatedLocations: Array<ShapeLocation<P>> = [];
    for (const location of shapeState.key_locations) {
      const extrapolatedPoints = location.extrapolation?.points;
      if (!extrapolatedPoints?.length) {
        updatedLocations.push(location);
        continue;
      }
      const extrapolatedKeyLocations: Array<ShapeLocationState<P>> =
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        extrapolatedPoints?.map((points, index) => ({
          frame_number: location.frame_number + index + 1,
          points,
          visibility: 1,
          source: location.extrapolation!.source,
        }));
      updatedLocations.push(
        omit(location, 'extrapolation'),
        ...extrapolatedKeyLocations,
      );
    }
    shapeState.key_locations = updatedLocations;
    return updatedLocations;
  }

  private answerIsNonBlank(
    answer:
      | ShapeLayerExternalObjectless<P, S>
      | StrictRecord<string, never>
      | undefined,
  ): answer is ShapeLayerExternalObjectless<P, S> {
    return !!answer && !isEmpty(answer);
  }

  private makeGroupExternal(
    shapeGroupOrder: ShapeGroupOrderState<S, P>,
    shapeIndexes: number[],
    sources: LocationSource[],
    groupType: string | null = null,
  ) {
    return {
      group_type: groupType,
      shapes: shapeIndexes.map((index) =>
        // @ts-ignore -- fix me
        this.shapeStateExternal(shapeGroupOrder.shapes[index], sources),
      ),
    };
  }

  private addShapeLocation(
    keyLocationExternal: ShapeLocationExternal<P>,
    shapeKeyLocations: Array<ShapeLocationState<P>>,
    sources: LocationSource[],
  ) {
    const source = sources[keyLocationExternal.source ?? -1];
    // Combine all adjacent key locations with ml-extrapolation source
    // (ML key locations) into a single key location
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (source?.name === 'ml-extrapolation') {
      const lastKeyLocation = shapeKeyLocations[shapeKeyLocations.length - 1];
      lastKeyLocation.extrapolation ??= {
        source,
        points: [],
      };
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- fix me!
      lastKeyLocation.extrapolation!.points.push(keyLocationExternal.points);
    } else {
      shapeKeyLocations.push({
        ...pickExisting(
          keyLocationExternal,
          'frame_number',
          'points',
          'visibility',
          'model_input_points',
        ),
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        ...(source && { source }),
      });
    }
  }
}

interface ShapeGroupExternalWithType<P extends PointType, S extends ShapeType>
  extends ShapeGroupExternal<P, S> {
  group_type: string;
}

function hasGroupType<P extends PointType, S extends ShapeType>(
  shapeGroupExternal: ShapeGroupExternal<P, S>,
): shapeGroupExternal is ShapeGroupExternalWithType<P, S> {
  return !!shapeGroupExternal.group_type;
}

export const answerHelper3D = new ShapeLayerAnswerHelper(contentUtils3D);

export const answerHelper2D = new ShapeLayerAnswerHelper(contentUtils2D);

/** @deprecated used for old projects without objects */
export function getShapeGroupOrderFromTaskObjectless(
  task: Task,
  output: AnnotationOutput,
) {
  const answer = task.answers_or_gold[output.name];
  return getShapeGroupOrderObjectless(answer, output);
}

/** @deprecated used for old projects without objects */
export function getShapeGroupOrderObjectless(
  answer: Answer,
  output: AnnotationOutput,
): ShapeGroupOrderState {
  if (isImageAnnotationOutput(output)) {
    const vectorTaggingAnswer = getVectorTaggingAnswer(answer);
    return answerHelper2D.answerToShapeGroupOrder(vectorTaggingAnswer);
  } else if (isPointCloudOutput(output)) {
    const pointCloudOnlyAnswer = filterOutOrthographicShapes(answer);
    return answerHelper3D.answerToShapeGroupOrder(pointCloudOnlyAnswer);
  }
  return answerHelper2D.answerToShapeGroupOrder(answer);
}

/** @deprecated used for old projects without objects */
export function getAnnotationAnswerObjectless(
  answer: Answer | undefined,
): ShapeLayerExternalObjectless<PointType, ShapeType> | undefined {
  const isValidObject = isDefined(answer) && !isEmpty(answer);
  if (!isValidObject) {
    return undefined;
  }

  const vectorTaggingAnswer = getVectorTaggingAnswer(answer);
  if (isDefined(vectorTaggingAnswer)) {
    return vectorTaggingAnswer;
  } else if (isDefined(answer.shape_groups)) {
    return answer;
  }

  return undefined;
}

/** @deprecated used for old projects without objects */
function getVectorTaggingAnswer(
  answer: Answer,
): VectorTaggingLayerExternal | undefined {
  return get(answer, ['layers', 'vector_tagging']);
}
